import { LiveEvent } from '@/pages/LiveEvent/store/liveEvent/types';
import { RLocationType, useLearningByIdQueryRemote } from '@/store/v2';
import { formatTimeDependingOnUserTimezone } from '@/utils/dates';
import { convertDateWithCustomTimezone } from '@/utils/time';
import { getIsLiveEventActive, getIsLiveEventEnded } from '@/pages/LiveEvent/store/utils';
import { useCurrentUser } from '@/store/currentUser';
import { useUserContentLastParticipations } from '@/store/v2/catalog-v2/use-user-last-participations';

type UsePracticalAssessmentViewerProps = {
  id: string;
};

type UsePracticalAssessmentViewerHook = {
  loading: boolean;
  liveEvent: LiveEvent | null;
};

export const useLiveEventViewer = ({ id }: UsePracticalAssessmentViewerProps): UsePracticalAssessmentViewerHook => {
  const { user } = useCurrentUser();
  const { data, loading } = useLearningByIdQueryRemote({
    variables: { id: String(id), includeProgress: true, includeLocations: true },
  });

  const liveEventData = data?.learning;

  // Use the correct URN format for participation lookup
  const contentURN = liveEventData?.urnV2 || `urn:collegial:self:learning:${id}`;

  // Get participation data for completion status
  const { participations } = useUserContentLastParticipations({
    userId: user?.id,
    contentURNs: [contentURN],
  });

  // Filter to get the participation for this specific live event
  const participation = participations?.find((p) => p.objectId === contentURN);

  const liveEventLocation = liveEventData?.locations?.[0];
  const startDateInUsersTimezone = loading
    ? null
    : convertDateWithCustomTimezone({
        date: new Date(liveEventLocation?.startDate as Date),
        customTimezoneOffsetInMinutes: Number(liveEventData?.meta?.timezone),
      });
  const endDateInUsersTimezone = loading
    ? null
    : convertDateWithCustomTimezone({
        date: new Date(liveEventLocation?.endDate as Date),
        customTimezoneOffsetInMinutes: Number(liveEventData?.meta?.timezone),
      });

  return {
    loading,
    liveEvent: {
      id: liveEventData?.id,
      spaceId: liveEventData?.spaceId,
      locationId: liveEventData?.locations?.[0]?.id,
      timezone: liveEventData?.meta?.timezone,
      title: liveEventData?.title || '',
      teaser: liveEventData?.teaser || '',
      image: liveEventData?.image || '',
      language: liveEventData?.languageId || 0,
      startDate: liveEventLocation?.startDate || null,
      endDate: liveEventLocation?.endDate || null,
      address: liveEventLocation?.address || '',
      type: liveEventLocation?.type || null,
      meta: liveEventData?.meta,
      myEnrollment: liveEventLocation?.myEnrollment || null,
      room: liveEventLocation?.room || '',
      recordingURL: liveEventLocation?.recordingURL || '',
      registration: liveEventLocation?.registration,
      // todo: determine where to get the completedAt value from
      completedAt: participation?.completedAt || undefined,
      progress: liveEventData?.progress
        ? {
            completed: liveEventData.progress.completed,
            completedPercentage: liveEventData.progress.completedPercentage,
          }
        : undefined,
      isOnline: liveEventLocation?.type === RLocationType.Online,
      isStartingSoon: loading
        ? false
        : getIsLiveEventActive({
            startDate: startDateInUsersTimezone as Date,
            endDate: endDateInUsersTimezone as Date,
          }),
      hasEnded: loading ? false : getIsLiveEventEnded({ endDate: endDateInUsersTimezone as Date }),
      dateAndTime: loading
        ? ''
        : formatTimeDependingOnUserTimezone({
            startTime: liveEventLocation?.startDate as Date,
            endTime: liveEventLocation?.endDate as Date,
            formatOptions: {
              showTimezone: true,
            },
          }),
      monthName: startDateInUsersTimezone?.toLocaleString('en-US', { month: 'short' }) || '',
      dayOfMonth: startDateInUsersTimezone?.getDate() || '',
    } as unknown as LiveEvent,
  };
};
